import React, { useEffect } from 'react';

import bgLogin from '@assets/img/arts/bg-login.png';
import d2d from '@assets/img/icons/d2d-new.svg';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { FormAlert } from '@components/Forms/FormAlert';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { TextInput } from '@components/Forms/TextInput';
import { Layout } from '@components/Layout';
import { useResendEmail } from '@hooks/mutation/useResendEmail';
import { useCountDown } from '@hooks/useCountDown';
import { useSearch } from '@hooks/useSearch';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings, createValidate, newEmailField } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { Link } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    resendTheMail: '認証メール再送信',
    send: '送信',
    willSendUrlToTheEmailAbove: '上記メールアドレスに認証用のURLを送信します',
    goBackToLogin: 'ログインに戻る',
    sentSuccessfully: '認証メールを成功に送信しました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.backgroundCenter,
    flex: 1,
    backgroundImage: `url(${bgLogin})`,
    paddingBottom: 130,
  },
  inner: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '0 1rem',
  },
  alertContainer: {
    ...common.flexColumn,
    justifyContent: 'center',
    height: 130,
    [theme.breakpoints.down('xs')]: {
      height: 65,
    },
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '90px 100px',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      padding: '20px',
    },
  },
  logo: {
    display: 'block',
    width: 130,
    height: 30,
    alignSelf: 'center',
    marginBottom: 50,
  },
  linkContainer: {
    ...common.flexInline,
    justifyContent: 'center',
    color: theme.palette.primary.main,
    marginTop: '70px',
  },
  helpLink: {
    ...common.baseButton,
    ...common.middleText,
    ...common.fontWeight600,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  title: {
    ...common.flexInline,
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '30px',
    color: theme.palette.text.primary,
    justifyContent: 'center',
    marginBottom: 50,
  },
}));

const schema = t.type({
  email: newEmailField(formStrings.email),
});

const query = t.type({
  email: h.omittable(t.string),
});

export const ResendEmail: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const mutation = useResendEmail();
  const timer = useCountDown(60);

  const { search } = useSearch(query);

  const initialValues: t.TypeOf<typeof schema> = {
    email: search.email || '',
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      timer.reset();
      timer.start();
    }
  }, [mutation.isSuccess]);

  return (
    <Layout title={strings.resendTheMail}>
      <div className={classes.container}>
        <div className={classes.inner}>
          <Formik
            validate={createValidate(schema)}
            initialValues={initialValues}
            onSubmit={async (values) => {
              await mutation.mutateAsync(h.unwrap(schema, values));
            }}
          >
            {() => (
              <>
                <div className={classes.alertContainer}>
                  <SubmittingOverlay />
                  <FormAlert dataId="ResendErrorAlert" mutation={mutation} />
                  <Alert
                    open={mutation.isSuccess}
                    severity="success"
                    onClose={mutation.reset}
                    scrollToCenter
                  >
                    {strings.sentSuccessfully}
                  </Alert>
                </div>
                <Form className={classes.formContainer}>
                  <div className={classes.title}>
                    <img src={d2d} className={classes.logo} alt="D2D Logo" />
                    <div style={{ width: '1rem' }} />
                    {strings.resendTheMail}
                  </div>

                  <TextInput
                    name="email"
                    autoComplete="current-email"
                    placeholder={formStrings.email}
                    noLabel
                    iconBefore={
                      <MailOutlineIcon
                        style={{ paddingRight: 10, width: '1.6rem', height: '1.6rem' }}
                      />
                    }
                  />

                  <Button
                    text={`${strings.send}${timer.isCounting ? ` (${timer.timeLeft})` : ''}`}
                    color="primary"
                    type="submit"
                    disabled={timer.isCounting}
                  />

                  <div className={clsx(classes.textGray400, classes.middleText)}>
                    {strings.willSendUrlToTheEmailAbove}
                  </div>

                  <div className={classes.linkContainer}>
                    <Link to="/auth/Login" className={classes.helpLink}>
                      {strings.goBackToLogin}
                    </Link>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </div>
      </div>
    </Layout>
  );
};
